import { mapState } from 'vuex';
import NiotaClassicApiService from '../../../../services/niotaClassicApiService';
import { localizeDate } from '../../../../utils/vuex';
import { themesMixin, vuetifyColors } from '../../../../utils/themes';
import MarkdownItVueLight from 'markdown-it-vue/dist/markdown-it-vue-light.umd.min.js';
import formMixin from '../../formMixin';
import max from 'lodash/max';
import min from 'lodash/min';
import mean from 'lodash/mean';
import get from 'lodash/get';
import set from 'lodash/set';

const techSpecKeys = [
  'Model',
  'Usage',
  'Configuration',
  'Connectivity',
  'Frequency',
  'TX Power',
  'TX Distance',
  'Power Supply',
  'Operating Temperature',
  'Security',
  'Mounting',
  'Dimensions',
  'Weight',
  'Standard',
  'Battery',
  'Battery Life',
  'Firmware',
  'Precision',
  'Installation Height',
  'Other',
];

export default {
  components: {
    MarkdownItVueLight,
  },
  mixins: [formMixin, themesMixin],
  props: {
    deviceId: {
      type: String,
      required: true,
    },
    datasourceType: {
      type: String,
      required: true,
    },
    deviceData: {
      type: Object,
      required: true,
    },
    packetsLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    devicePackets: {
      type: Array,
      required: false,
      default: new Array(),
    },
    keysDatasets: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    parsedPackets: {
      type: Object,
      required: false,
      default: null,
    },
    btnDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: function () {
    return {
      deviceTypeActivities: null,
      tabItem: 'details',
      // showParsed: false,
      validStatistics: false,
      period_statistics: null,
      period_older_statistics: null,
      packets_increase_ratio: null,
      sending_rate: 0,
      sending_rate_older: 0,

      lastRSSI: null,
      rssiIcon: 'signal-slash',
      booleanBatteryLevelFlag: false,
      battery_warning: false,
      battery_alert: false,
      battery_level: null,
      battery_warning_level: null,
      battery_alert_level: null,
      operationalStatusVerification: { status: true },
      payloadStatistics: {},
      parsedPacketFound: false,
      parsedPacketsOptions: {},
      allRelevantKeysRaw: [],

      packets_statistics: null,
      packets_statistics_latest_24hrs: null,
      packets_statistics_previous_24hrs: null,
      default_metrics_length: { length: 1, name: 'Last 24h' },
      metrics_length: { length: 1, name: 'Last 24h' },
      max: null,
      noPreviousDataFound: false,
      average_packets: null,
      noDataFound: false,
      transmissionGraphLoading: false,
      spreading_factor_average: null,
      shadeBackgroundColor1: null,
      shadeBackgroundColor2: null,
      status: null,
      remoteLink: null,
      isLoadingMetrics: true,
      isLoadingSnr: true,
      hourlyPacketsgraph: {},
      hourlyPacketsgraphOptions: {
        scales: {
          yAxes: [
            {
              ticks: {
                min: 0,
              },
            },
          ],
        },
      },
      snrGraph: {},
      spreadingFactorGraph: {},
      start: new Date(),
      techSpecKeys: techSpecKeys,
      switchDate: false,
      spreadingFactorGraphOptions: {
        legend: false,
        scales: {
          yAxes: [
            {
              ticks: {
                min: 7,
                max: 12,
                stepSize: 0.5,
              },
            },
          ],
        },
      },
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'permissions']),
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    applicationTitle() {
      if (get(this.deviceData, 'application.title')) {
        return get(this.deviceData, 'application.title');
      }
      return null;
    },
    applicationLink() {
      if (get(this.deviceData, 'application.id')) {
        return `/niota-classic/applications/${get(this.deviceData, 'application.id')}/details`;
      }
      return null;
    },
    deviceTypeTitle() {
      if (get(this.deviceData, 'deviceType.title')) {
        return get(this.deviceData, 'deviceType.title');
      }
      return null;
    },
    deviceTypeLink() {
      if (this.deviceData.deviceTypeId) {
        return `/niota-classic/device-types/${this.deviceData.deviceTypeId}/details`;
      }
      return null;
    },
  },
  watch: {
    devicePackets() {
      this.setDevicePackets();
    },
    parsedPackets() {
      this.setParsedPackets();
    },
    color: function () {
      let graphData = get(this.hourlyPacketsgraph, 'individual[0].data');
      let snrGraphData = get(this.snrGraph, 'individual[0].data');
      let spreadingFactorGraphData = get(this.spreadingFactorGraph, 'individual[0].data');
      if (graphData && graphData.length) {
        set(this.hourlyPacketsgraph, 'individual[0].color', this.vuetifyColorHex());
      }
      if (snrGraphData && snrGraphData.length) {
        set(this.snrGraph, 'common.color', this.vuetifyColorHex());
      }
      if (spreadingFactorGraphData && spreadingFactorGraphData.length) {
        set(this.spreadingFactorGraph, 'common.color', this.vuetifyColorHex());
      }
    },
  },
  mounted() {
    this.initData();
  },
  methods: {
    localizeDate,
    initData() {
      this.setDevice(this.deviceData);
      this.setDevicePackets();
    },
    fetchDeviceStatus() {
      NiotaClassicApiService.deviceFetchStatus(this.deviceId)
        .then((res) => {
          this.remoteLink = res.data.link;
          this.status = res.data.status;
        })
        .catch((error) => {
          this.remoteLink = null;
          this.status = null;
          // this.$snack(this.$i18n_t('NiotaClassic.Devices.snacks.deviceStatusNotFound'), 'error');
          console.error(error);
        });
    },
    fetchDevicePacketsMetrics() {
      this.isLoadingMetrics = true;
      this.isLoadingSnr = true;
      this.noDataFound = false;
      this.noPreviousDataFound = false;
      NiotaClassicApiService.fetchDevicePacketsMetrics(this.deviceData.id, this.metrics_length.length)
        .then(async (res) => {
          if (!res.metrics.length) {
            this.noDataFound = true;
          } else {
            if (this.deviceData.properties && this.deviceData.properties.enable_statistics) {
              await this.fetchDevicePacketsStatistics();
            }
            this.setDeviceMetricsPackets(res);
          }
        })
        .catch((res) => {
          this.$snack(this.$i18n_t('NiotaClassic.Devices.snacks.errorLoadingDevicePacketsMetrics'), 'error');
          console.error(res);
        })
        .finally(() => (this.isLoadingMetrics = false));
    },
    async fetchDevicePacketsStatistics() {
      this.packets_statistics = false;
      await NiotaClassicApiService.fetchDevicePacketsStatistics(this.deviceData.id)
        .then((res) => {
          this.packets_statistics = res;
          this.packets_statistics_latest_24hrs = res.statistics.latest_24hrs;
          this.packets_statistics_previous_24hrs = res.statistics.previous_24hrs;
        })
        .catch((res) => {
          this.$snack(this.$i18n_t('NiotaClassic.Devices.snacks.errorLoadingDevicePacketsStatistics'), 'error');
          console.error(res);
        });
    },
    fetchDeviceTransmissionMetrics() {
      this.transmissionGraphLoading = true;
      NiotaClassicApiService.fetDeviceTransmissionMetrics(this.deviceData.id, {
        period: this.metrics_length.length,
      })
        .then((res) => {
          // if (this.deviceData.properties && this.deviceData.properties.enable_SFGraph) {
          this.setDeviceTransmissionMetrics(res.data);
          // }
        })
        .catch((res) => {
          console.error(res);
        })
        .finally(() => (this.transmissionGraphLoading = false));
    },
    setDevice(device) {
      this.deviceData = device;
      this.initDeviceProperties(device);
      if (!get(this.deviceData, 'properties.minimum_packets_threshold') && get(device, 'deviceType.properties')) {
        this.deviceData.properties.minimum_packets_threshold = get(device, 'deviceType.properties.expected_packets');
      }
      let type = get(device, 'originator.datasource.type');
      if (type === 'firefly_mqtt' || type === 'firefly') {
        this.fetchDeviceStatus();
      }
      this.parsedPacketsOptions = { limit: 10, variables: [] };
      let deviceTypeVariables = device.deviceType ? this.deviceTypeVariables(device.deviceType) : [];
      // if a parser exists, then call the parsed packets route
      if (device.deviceType && deviceTypeVariables.length) {
        this.parsedPacketsOptions.variables = deviceTypeVariables;
        this.batteryLevelSettings(device);
        this.$emit('loadParsedPackets', this.parsedPacketsOptions);
      }
    },
    initDeviceProperties(device) {
      if (!device.properties) {
        this.deviceData.properties = {};
      }
      if (get(device, 'properties.enable_statistics') === undefined) {
        this.deviceData.properties.enable_statistics = true;
      }
      if (get(device, 'properties.enable_SFGraph') === undefined) {
        this.deviceData.properties.enable_SFGraph = true;
      }
    },
    deviceTypeVariables(deviceType) {
      let variableKeys = [];
      const type = get(deviceType, 'parser.type');
      //parser type single
      if (type === 'single' || type === 'function') {
        let variables = get(deviceType, 'parser.config.variables');
        variableKeys = this.parserVariables(variables || {});
      }
      //parser type multi
      if (type === 'multi') {
        let parsers = Object.keys(get(deviceType, 'parser.config') || {});
        parsers.forEach((parser_n) => {
          if (deviceType.parser.config[parser_n] && deviceType.parser.config[parser_n].variables) {
            let indivParserVariables =
              parser_n !== '__switch' ? deviceType.parser.config[parser_n].variables || [] : [];
            Object.keys(indivParserVariables).forEach((variable) => {
              variableKeys.push({
                variable: indivParserVariables[variable]['name'] ? indivParserVariables[variable]['name'] : '',
                parser: parser_n,
                value_type_name: this.variableTypeFinder(indivParserVariables[variable]),
              });
            });
          }
        });
      }
      return variableKeys;
    },
    parserVariables(deviceTypeVariables) {
      let variables = [];
      Object.keys(deviceTypeVariables).forEach((key) => {
        variables.push({
          variable: deviceTypeVariables[key]['name'] ? deviceTypeVariables[key]['name'] : '',
          value_type_name: this.variableTypeFinder(deviceTypeVariables[key]),
        });
      });
      return variables;
    },
    variableTypeFinder(variable) {
      let variableType = '';
      if (variable) {
        if (variable['suffix']) {
          variableType = variable['suffix'];
          if (variable['value_type'] && variable['value_type'].name) {
            variableType = variable['value_type'].name;
          }
        }
        // for boolean case
        if (variable['value_type']) {
          if (variable['value_type'] && variable['value_type'].name) {
            variableType = variable['value_type'].name;
          }
        }
      }
      return variableType;
    },
    batteryLevelSettings(device) {
      const deviceProperties = device.properties;
      const deviceTypeProperties = get(device, 'deviceType.properties');
      if (deviceTypeProperties.battery_type === 'Boolean') {
        return this.booleanBatteryLevel(device);
      } else {
        this.booleanBatteryLevelFlag = false;
        this.battery_warning_level =
          deviceProperties.battery_warning_level || deviceProperties.battery_warning_level === 0
            ? deviceProperties.battery_warning_level
            : deviceTypeProperties.battery_warning_level;
        this.battery_alert_level =
          deviceProperties.battery_alert_level || deviceProperties.battery_alert_level === 0
            ? deviceProperties.battery_alert_level
            : deviceTypeProperties.battery_alert_level;
      }
    },
    booleanBatteryLevel(device) {
      const deviceTypeProperties = get(device, 'deviceType.properties');
      this.booleanBatteryLevelFlag = true;
      this.battery_warning_level = deviceTypeProperties.battery_warning_level;
      this.battery_alert_level = deviceTypeProperties.battery_alert_level;
    },
    setBatteryLevel(packets) {
      if (packets.data && get(this.deviceData, 'deviceType.properties.battery_level')) {
        this.battery_level = packets.data[this.deviceData.deviceType.properties.battery_level][0].orig_value;
        this.checkBatteryLevel();
      }
    },
    checkBatteryLevel() {
      this.battery_warning = false;
      this.battery_alert = false;
      if (!this.booleanBatteryLevelFlag) {
        if (this.battery_level || this.battery_level === 0) {
          //Toggle Warning
          if (this.battery_level <= this.battery_warning_level) {
            this.battery_warning = true;
          }
          //Toggle Alert: is higher than warning
          if (this.battery_level <= this.battery_alert_level) {
            this.battery_alert = true;
          }
        }
      } else {
        if (this.battery_level || this.battery_level === false) {
          //Toggle Warning
          if (this.battery_level === this.battery_warning_level) {
            this.battery_warning = true;
          }
          //Toggle Alert: is higher than warning
          if (this.battery_level === this.battery_alert_level) {
            this.battery_alert = true;
          }
        }
      }
      // Toaster for user notification
      if (this.battery_alert) {
        this.$snack(this.$i18n_t('NiotaClassic.Devices.snacks.battryAlertMsg'), 'info');
      }
      if (this.battery_warning) {
        this.$snack(this.$i18n_t('NiotaClassic.Devices.snacks.battryWarningMsg'), 'info');
      }
    },
    setDevicePackets() {
      if (this.devicePackets.length) {
        this.payloadStatistics = this.packetsStatistics(this.devicePackets);
        if (this.deviceData.properties) {
          this.checkOperationalStatus(this.deviceData, this.devicePackets, this.operationalStatusVerification);
        }
        if (this.devicePackets.find((packet) => packet.parsed)) {
          this.parsedPacketFound = true;
          if (this.deviceData.deviceType) {
            this.filterHiddenParsedPacketsVariables(this.devicePackets);
          }
        }
        this.setLastRSSI();
        this.selectMetricsLength(this.default_metrics_length);
      }
    },
    packetsStatistics(packets) {
      let spreading_factors = [];
      let bWs = [];
      let sizes = [];
      let statistics = {};
      packets.forEach((packet) => {
        if (packet.source === 'firefly' || packet.source === 'firefly_mqtt') {
          spreading_factors.push(packet.source_data.spreading_factor);
          bWs.push(packet.source_data.bandwidth);
          sizes.push(packet.source_data.server_data.size);
        }
      });
      if (spreading_factors.length && bWs.length && sizes.length) {
        statistics = {
          sf: this.arrayBasicStatistics(spreading_factors),
          bw: this.arrayBasicStatistics(bWs),
          payloadSize: this.arrayBasicStatistics(sizes),
        };
      }
      return statistics;
    },
    arrayBasicStatistics(array) {
      return {
        max: max(array),
        min: min(array),
        mean: array.reduce((a, b) => a + b, 0) / array.length,
      };
    },
    checkOperationalStatus(device, packets, operationalStatusVerification, applicationDevice = false) {
      // if the device is in transit(value: 1) or ready for operation(value: 2) and there are packets the user should change the operational status
      let errorText = applicationDevice ? `${device.title.substr(0, 16)} - ` : '';
      const verificationStatus = {
        status: false,
        error: `${errorText}${this.$i18n_t('NiotaClassic.Devices.details.batteryErrorMsg')}`,
      };
      if (device.properties.operational_status) {
        operationalStatusVerification =
          device.properties.operational_status.value !== 3 && packets.length ? verificationStatus : { status: true };
      } else {
        operationalStatusVerification = { status: true };
      }
      // toaster with error
      if (!operationalStatusVerification.status) {
        this.$snack(operationalStatusVerification.error, 'error');
      }
    },
    filterHiddenParsedPacketsVariables(packets) {
      packets.forEach((packet) => this.filterHiddenPacketVariables(packet.parsed));
    },
    filterHiddenPacketVariables(parsedPacket) {
      let variables = get(this.deviceData, 'deviceType.parser.config.variables') || {};
      const deviceTypeVariables = variables;
      const parsedPacketKeys = Object.keys(parsedPacket || {});

      if (deviceTypeVariables && parsedPacketKeys.length) {
        parsedPacketKeys
          .filter((key) => deviceTypeVariables[key])
          .filter((key) => deviceTypeVariables[key].type === 'hidden')
          .forEach((key) => delete parsedPacket[key]);
      }
      if (get(this.deviceData, 'deviceType.parser.type') === 'multi') {
        let parserConfig = get(this.deviceData, 'deviceType.parser.config');
        let parsers = Object.keys(parserConfig);
        parsers.forEach((parser) => {
          let parserVariables = parserConfig[parser].variables || [];
          parsedPacketKeys
            .filter((key) => parserVariables[key])
            .filter((key) => parserVariables[key].type === 'hidden')
            .forEach((key) => delete parsedPacket[key]);
        });
      }
    },
    setLastRSSI() {
      if (!this.devicePackets[0].source_data) {
        this.lastRSSI = 0;
        return;
      }
      let sourceData = this.devicePackets[0].source_data;
      this.setLastBatteryLevel(sourceData);
      // SIGFOX
      if (typeof sourceData.rssi !== 'undefined') {
        this.lastRSSI = sourceData.rssi;
      }
      // TTN
      if (
        typeof sourceData.metadata !== 'undefined' &&
        typeof sourceData.metadata.gateways !== 'undefined' &&
        sourceData.metadata.gateways.length
      ) {
        let mediumValues = this.calcualteMediumReceptionValues(sourceData.metadata.gateways);
        this.lastRSSI = mediumValues.rssi;
      }
      // FIREFLY
      if (
        typeof sourceData.server_data !== 'undefined' &&
        typeof sourceData.server_data.gwrx !== 'undefined' &&
        sourceData.server_data.gwrx.length
      ) {
        let mediumValues = this.calcualteMediumReceptionValues(sourceData.server_data.gwrx);
        this.lastRSSI = mediumValues.rssi;
      }

      //Actility
      if (get(this.devicePackets[0], 'source') === 'actility') {
        if (this.devicePackets[0].signal) {
          let signal = this.devicePackets[0].signal;
          this.lastRSSI = signal.rssi;
        }
      }
    },
    setLastBatteryLevel(sourceData) {
      if (sourceData.parsed && get(this.deviceData, 'deviceType.properties.battery_level')) {
        this.battery_level = sourceData.parsed[get(this.deviceData, 'deviceType.properties.battery_level')];
        this.checkBatteryLevel();
      }
    },
    calcualteMediumReceptionValues(gwrx) {
      let rssis = [];
      let lsnrs = [];
      gwrx.forEach((gw) => {
        rssis.push(gw.rssi);
        lsnrs.push(gw.lsnr);
      });
      return { rssi: mean(rssis), lsnr: mean(lsnrs) };
    },

    setPeriodStatistics(period_statistics, period_older_statistics, packets_increase_ratio) {
      this.validStatistics = false;
      this.period_statistics = period_statistics;
      this.period_older_statistics = period_older_statistics;
      this.packets_increase_ratio = { value: Math.abs(packets_increase_ratio), increase: packets_increase_ratio >= 0 };
      if (period_statistics.valid) {
        this.sending_rate = period_statistics.sending_rate;
      }
      if (period_older_statistics.valid) {
        this.sending_rate_older = period_older_statistics.sending_rate;
      }
      if (this.packets_increase_ratio && period_statistics.valid) {
        this.validStatistics = true;
      }
    },
    setMetricStatistics(annotations) {
      let statistics = this.packets_statistics.statistics;
      switch (this.metrics_length.length) {
        case 1:
          this.setPeriodStatistics(
            statistics.latest_24hrs,
            statistics.previous_24hrs,
            statistics.increase_ratio.last24hrs
          );
          return this.metricsStatisticsAnnotations(statistics.latest_24hrs, statistics.previous_24hrs, annotations);
        case 7:
          this.setPeriodStatistics(
            statistics.latest_week,
            statistics.previous_week,
            statistics.increase_ratio.lastWeek
          );
          return this.metricsStatisticsAnnotations(statistics.latest_week, statistics.previous_week, annotations);
        case 31:
          this.setPeriodStatistics(
            statistics.latest_month,
            statistics.previous_month,
            statistics.increase_ratio.lastMonth
          );
          return this.metricsStatisticsAnnotations(statistics.latest_month, statistics.previous_month, annotations);
      }
    },
    metricsStatisticsAnnotations(packets_statistics, packets_statistics_previous, annotations) {
      let average = 0;
      let maximum = null;
      if (packets_statistics.valid) {
        // let rate = packets_statistics.rate;
        average = packets_statistics.average;
        // let average_previous = packets_statistics_previous.average;
        maximum =
          packets_statistics_previous.max > packets_statistics.max
            ? packets_statistics_previous.max
            : packets_statistics.max;
        // If the threshold is so big compared to all averages
        if (get(this.deviceData, 'properties.minimum_packets_threshold')) {
          const threshold = get(this.deviceData, 'properties.minimum_packets_threshold');
          if (threshold > max) {
            maximum = threshold;
          }
        }
        this.max = maximum;
        // average horizontal line for last 24hr/week/month
        annotations.push({
          value: average,
          label: `${this.$i18n_t('NiotaClassic.Devices.details.average')} (${average.toFixed(2)}/d)`,
          labelPadding: -250,
        });
        // average horizontal line for before last 24hr/week/month
        if (packets_statistics_previous.valid) {
          annotations.push({
            value: packets_statistics_previous.average,
            label: `${this.$i18n_t(
              'NiotaClassic.Devices.details.previousAverage'
            )} (${packets_statistics_previous.average.toFixed(2)}/d)`,
            labelPadding: 250,
          });
        }
      }
      return { annotations, average, max: maximum };
    },
    setDeviceMetricsPackets(data) {
      let labels = this.getDevicePacketsMetricsLabels(data.metrics);
      let datasets = this.getDevicePacketsMetricsDatasets(data);
      let annotations = [];
      if (this.packets_statistics) {
        let metricStatistics = this.setMetricStatistics(annotations);
        annotations = metricStatistics.annotations;
        let threshold = get(this.deviceData, 'properties.minimum_packets_threshold');
        if (threshold && this.metrics_length.length !== 1) {
          annotations.push({
            value: threshold,
            label: `${this.$i18n_t('NiotaClassic.Devices.table.threshold')} (${threshold}/d)`,
            color1: '#f00',
            color2: '#ff3d00a1',
          });
        }
        set(this.hourlyPacketsgraphOptions, 'max', metricStatistics.max);
        set(this.hourlyPacketsgraphOptions, 'annotations', metricStatistics.annotations);
        this.average_packets = metricStatistics.average;
      }
      if (!labels.length) {
        labels = this.getDevicePacketsMetricsLabels(
          this.setChartLabelsFromStatistics(this.packets_statistics.data.time),
          true
        );
      }
      this.updateGraph('packets', labels, datasets);
    },
    getDevicePacketsMetricsLabels(metrics, fromStatistics = false) {
      let labels = metrics.map((k) => {
        let date = new Date(fromStatistics ? k : k.date);
        let day = date.getDate();
        let month = date.getMonth() + 1;
        let hours = `0${date.getHours()}`.slice(-2);
        return this.metrics_length.length === 1 ? `${hours}:00` : `${day}.${month}`;
      });
      return labels;
    },
    getDevicePacketsMetricsDatasets(data) {
      let datasetsData = data.metrics.map((matrix) => {
        return Number(matrix.value);
      });
      let chartDatasets = [
        {
          label: this.$i18n_t('NiotaClassic.Devices.details.latestPackets'),
          data: datasetsData,
        },
        this.packets_statistics ? this.setPreviousDataset() : undefined,
      ];

      return chartDatasets.filter((x) => x);
    },
    setPreviousDataset() {
      let datasetsData_previous = [];
      switch (this.metrics_length.length) {
        case 1:
          datasetsData_previous = get(this.packets_statistics, 'data.data.previous_24hrs');
          break;
        case 7:
          datasetsData_previous = get(this.packets_statistics, 'data.data.previous_week');
          break;
        case 31:
          datasetsData_previous = get(this.packets_statistics, 'data.data.previous_month');
          break;
      }
      // it is used if any previous data found
      if (!datasetsData_previous.length || !datasetsData_previous.filter((x) => x).length) {
        this.noPreviousDataFound = true;
      }
      return {
        label: this.$i18n_t('NiotaClassic.Devices.details.previousPackets'),
        data: datasetsData_previous,
        order: 1,
      };
    },
    updateGraph(type, labels, datasets) {
      let graphData = {
        common: { color: false, labels: labels },
        individual: [],
      };
      if (type === 'packets') {
        datasets.forEach((dataset, index) => {
          graphData.individual.push({
            data: dataset.data,
            label: dataset.label,
            color: index === 0 ? this.vuetifyColorHex() : '#c2c2c2',
          });
        });
        this.hourlyPacketsgraph = graphData;
      }
      if (type === 'snr') {
        graphData.common.color = this.vuetifyColorHex();
        datasets.forEach((dataset) => {
          graphData.individual.push({
            data: dataset.data,
            label: dataset.label,
          });
        });
        this.snrGraph = graphData;
      }
      if (type === 'sf') {
        graphData.common.color = this.vuetifyColorHex();
        // this.spreadingFactorGraph = {
        //   data: datasets[0].data,
        //   label: 'SF',
        //   color: this.vuetifyColorHex(),
        //   dataLabels: labels,
        // };
        datasets.forEach((dataset) => {
          graphData.individual.push({
            data: dataset.data,
            label: dataset.label,
          });
        });
        this.spreadingFactorGraph = graphData;
      }
    },
    setChartLabelsFromStatistics(allLabels) {
      switch (this.metrics_length.length) {
        case 1:
          return allLabels.latest_24hrs.length ? allLabels.latest_24hrs : allLabels.previous_24hrs;
        case 7:
          return allLabels.latest_week.length ? allLabels.latest_week : allLabels.previous_week;
        case 31:
          return allLabels.latest_month.length ? allLabels.latest_month : allLabels.previous_month;
      }
    },
    selectMetricsLength(selectedTimeFrame) {
      this.metrics_length = selectedTimeFrame;
      this.fetchDevicePacketsMetrics();
      this.fetchDeviceTransmissionMetrics();
    },
    setDeviceTransmissionMetrics(data) {
      if (
        get(data, 'metrics.SF') &&
        get(data, 'metrics.SF').length &&
        get(data, 'metrics.rssi') &&
        get(data, 'metrics.rssi').length &&
        get(data, 'metrics.snr') &&
        get(data, 'metrics.snr').length
      ) {
        let labels = this.getDeviceTransmissionMetricsLabels(data.metrics);
        let { datasets, datasetsSF } = this.getDeviceTransmissionMetricsDatasets(data);
        this.updateGraph('snr', labels, datasets);
        this.updateGraph('sf', labels, datasetsSF);
      }
    },
    getDeviceTransmissionMetricsLabels(metrics) {
      let labels = metrics.snr.map((k) => {
        let date = new Date(k.date);
        let day = date.getDate();
        let month = date.getMonth() + 1;
        let hours = `0${date.getHours()}`.slice(-2);
        return this.metrics_length.length === 1 ? `${hours}:00` : `${day}.${month}`;
      });
      return labels;
    },
    getDeviceTransmissionMetricsDatasets(data) {
      let rssi = get(data, 'metrics.rssi');
      let snr = get(data, 'metrics.snr');
      let sf = get(data, 'metrics.SF');
      let average = get(data, 'metricsSF.average');
      let rssiData = null;
      let snrData = null;
      let SFData = null;
      if (rssi) {
        rssiData = rssi.map((matrix) => {
          if (matrix.value || matrix.value === 0) {
            return Number(matrix.value);
          }
        });
      }
      if (snr) {
        snrData = data.metrics.snr.map((matrix) => {
          if (matrix.value || matrix.value === 0) {
            return Number(matrix.value);
          }
        });
      }
      if (sf) {
        SFData = data.metrics.SF.map((matrix) => {
          if (matrix.value || matrix.value === 0) {
            return Number(matrix.value);
          }
        });
      }

      if (average) {
        this.spreading_factor_average = average.toFixed(2);
      }
      let datasets = [
        {
          label: 'rssi',
          data: rssiData,
        },
        {
          label: 'snr',
          data: snrData,
        },
      ];
      let datasetsSF = [
        {
          label: 'SF',
          data: SFData,
        },
      ];
      return { datasets, datasetsSF };
    },
    setParsedPackets() {
      this.setBatteryLevel(this.parsedPackets);
    },
    getColor(type) {
      return vuetifyColors[type] || this.vuetifyColorHex();
    },
    datePicked(dateObject) {
      this.start = new Date(dateObject.from * 1000);
      this.$emit('fetchDevicePackets', this.start.toISOString());
    },
    getRssiValue() {
      if (Number.isInteger(this.lastRSSI) && this.lastRSSI > -60) {
        this.rssiIcon = 'signal';
        return this.$i18n_t('NiotaClassic.Devices.details.excellent');
      } else if (this.lastRSSI < -60 && this.lastRSSI > -90) {
        this.rssiIcon = 'signal-3';
        return this.$i18n_t('NiotaClassic.Devices.details.good');
      } else if (this.lastRSSI < -90 && this.lastRSSI > -110) {
        this.rssiIcon = 'signal-2';
        return this.$i18n_t('NiotaClassic.Devices.details.average');
      } else if (this.lastRSSI < -110 && this.lastRSSI > -150) {
        this.rssiIcon = 'signal-1';
        return this.$i18n_t('NiotaClassic.Devices.details.low');
      } else if (this.lastRSSI < -150) {
        this.rssiIcon = 'signal-slash';
        return this.$i18n_t('NiotaClassic.Devices.details.none');
      }
      return this.$i18n_t('NiotaClassic.Devices.details.none');
    },
    getBatteryLevel() {
      let text = '';
      if (this.battery_level || this.battery_level === 0 || this.battery_level === false) {
        text = this.battery_level;
      }
      if (get(this.deviceData, 'deviceType.properties.battery_type')) {
        text += get(this.deviceData, 'deviceType.properties.battery_type');
      }
      return text;
    },
    getBatteryWarningLevel() {
      let text = '';
      if (this.battery_warning_level || this.battery_warning_level === 0) {
        text = this.battery_warning_level;
        let battery_type = get(this.deviceData, 'deviceType.properties.battery_type');
        if (battery_type !== 'Boolean' && battery_type) {
          text += this.deviceData.deviceType.properties.battery_type;
        }
      } else {
        text = '-';
      }
      return text;
    },
    getBatteryAlertLevel() {
      let text = '';
      if (this.battery_alert_level || this.battery_alert_level === 0) {
        text = this.battery_alert_level;
        let battery_type = get(this.deviceData, 'deviceType.properties.battery_type');
        if (battery_type !== 'Boolean' && battery_type) {
          text += this.deviceData.deviceType.properties.battery_type;
        }
      } else {
        text = '-';
      }
      return text;
    },
    operationalStatus(deviceData) {
      if (get(deviceData, 'properties.operational_status.text')) {
        return get(deviceData, 'properties.operational_status.text');
      }
      return '';
    },
  },
};
